import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createJob,
  getJob,
  JobPostingDetails,
  updateJob,
} from '../services/adminApi';

const emptyJob: Omit<JobPostingDetails, 'id'> = {
  title: '',
  department: '',
  location: '',
  employmentType: '',
  isOpen: true,
  summary: '',
  responsibilities: '',
  qualifications: '',
  closesAt: '',
};

const JobEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Omit<JobPostingDetails, 'id'>>(emptyJob);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadJob = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getJob(id);
        setJob({
          title: data.title,
          department: data.department,
          location: data.location,
          employmentType: data.employmentType,
          isOpen: data.isOpen,
          summary: data.summary,
          responsibilities: data.responsibilities,
          qualifications: data.qualifications,
          closesAt: data.closesAt ? data.closesAt.slice(0, 10) : '',
        });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('pages.jobEditorLoadFailed', 'Failed to load job'));
      } finally {
        setLoading(false);
      }
    };

    void loadJob();
  }, [id, t]);

  const handleFieldChange = (field: keyof Omit<JobPostingDetails, 'id'>, value: string | boolean) => {
    setJob((current) => ({ ...current, [field]: value } as Omit<JobPostingDetails, 'id'>));
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const payload = {
        ...job,
        closesAt: job.closesAt || null,
      };

      if (id) {
        await updateJob(id, payload);
      } else {
        await createJob(payload);
      }

      navigate('/admin/jobs');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('pages.jobEditorSaveFailed', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{id ? t('pages.jobEditorTitleEdit') : t('pages.jobEditorTitleNew')}</h2>
        <p>{t('pages.jobEditorDescription')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <div>{t('common.loading')}</div>
      ) : (
        <form className="form-grid" onSubmit={handleSave}>
          <div className="form-group">
            <label>{t('pages.jobEditorFieldTitle')}</label>
            <input
              className="form-control"
              value={job.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('pages.jobEditorFieldDepartment')}</label>
            <input
              className="form-control"
              value={job.department}
              onChange={(e) => handleFieldChange('department', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.jobEditorFieldLocation')}</label>
            <input
              className="form-control"
              value={job.location}
              onChange={(e) => handleFieldChange('location', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.jobEditorFieldEmploymentType')}</label>
            <input
              className="form-control"
              value={job.employmentType}
              onChange={(e) => handleFieldChange('employmentType', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={job.isOpen}
                onChange={(e) => handleFieldChange('isOpen', e.target.checked)}
              />{' '}
              {t('pages.jobEditorFieldIsOpen')}
            </label>
          </div>

          <div className="form-group">
            <label>{t('pages.jobEditorFieldSummary')}</label>
            <textarea
              className="form-control"
              rows={3}
              value={job.summary}
              onChange={(e) => handleFieldChange('summary', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.jobEditorFieldResponsibilities')}</label>
            <textarea
              className="form-control"
              rows={4}
              value={job.responsibilities}
              onChange={(e) => handleFieldChange('responsibilities', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.jobEditorFieldQualifications')}</label>
            <textarea
              className="form-control"
              rows={4}
              value={job.qualifications}
              onChange={(e) => handleFieldChange('qualifications', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.jobEditorFieldClosesAt')}</label>
            <input
              type="date"
              className="form-control"
              value={job.closesAt ?? ''}
              onChange={(e) => handleFieldChange('closesAt', e.target.value)}
            />
          </div>

          <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.loading') : t('common.save')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/jobs')}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default JobEditor;
